import { DEFAULT_LOCALE } from 'config'
import {
  format,
  formatDistanceToNowStrict,
  parse,
  isBefore,
  isEqual,
  isAfter,
  startOfDay,
  isToday,
  formatDistance,
} from 'date-fns'
import { enGB, es, he, pt, ro, zhCN } from 'date-fns/locale'

import { isString } from '@/utils/helpers/typeChecking'

const dateFnsLocals = {
  en: enGB,
  'es-cl': es,
  'es-co': es,
  'es-es': es,
  'es-do': es,
  'he-il': he,
  'pt-pt': pt,
  'ro-ro': ro,
  'zh-cn': zhCN,
}

// Consider using these formats instead of the above
export const IntlFormats = {
  DATE_ONLY_SHORT: 'P',
  DATE_WITH_TIME_SHORT: 'Pp',
  DATE_WITH_TIME_LONG: 'PPPP',
  DAY_WEEK_SHORT: 'EEE',
  DAY_WEEK_LONG: 'EEEE',
  MONTH_SHORT: 'MMM',
  MONTH_LONG: 'MMMM',
  YEAR_SHORT: 'yy',
  YEAR_LONG: 'yyyy',
  DATE_WITH_MONTH_LONG: 'MMMM d, yyyy',
  DATE_WITH_MONTH_LONG_VIEW_MODE: 'd MMMM yyyy',
}

const TIME_ZONE_UTC = 'UTC'
const ISOFormats = {
  DATE: 'yyyy-MM-dd',
  DATE_TIME: "yyyy-MM-dd'T'HH:mm",
}

const getDateFnsLocal = () => {
  const currentLocale = localStorage.getItem('current-lang') || DEFAULT_LOCALE
  return dateFnsLocals[currentLocale.toLowerCase()]
}

const parseDate = (dateIsoString) => {
  if (isString(dateIsoString)) {
    return dateIsoString.length === 10
      ? parse(dateIsoString, ISOFormats.DATE, new Date())
      : new Date(dateIsoString)
  }
  return dateIsoString
}

export const formatDate = (date) => {
  if (!date) return null
  return format(parseDate(date), IntlFormats.DATE_ONLY_SHORT, {
    locale: getDateFnsLocal(),
  })
}

export const formatDateWithMonthLong = (date, isViewMode = false) => {
  if (!date) return null
  return format(
    parseDate(date),
    isViewMode
      ? IntlFormats.DATE_WITH_MONTH_LONG_VIEW_MODE
      : IntlFormats.DATE_WITH_MONTH_LONG,
    {
      locale: getDateFnsLocal(),
    },
  )
}

export const formatDateTime = (date) => {
  if (!date) return null
  return format(parseDate(date), IntlFormats.DATE_WITH_TIME_SHORT, {
    locale: getDateFnsLocal(),
  })
}

export const formatDateISO = (date) => {
  if (!date) return null
  return format(parseDate(date), ISOFormats.DATE, {
    timeZone: TIME_ZONE_UTC,
  })
}

export const formatDateTimeISO = (date) => {
  if (!date) return null
  return format(parseDate(date), ISOFormats.DATE_TIME, {
    timeZone: TIME_ZONE_UTC,
  })
}

export const isDateInThePast = (date) => {
  if (!date) return null
  const parsed = parseDate(date)
  return isBefore(startOfDay(parsed), startOfDay(new Date()))
}

export const isDateBeforeDate = (date, compareDate) => {
  if (!date) return null
  const parsedDate = parseDate(date)
  const parsedCompareDate = parseDate(compareDate)
  return isBefore(startOfDay(parsedDate), startOfDay(parsedCompareDate))
}

export const isDateAfterDate = (date, compareDate) => {
  if (!date) return null
  const parsedDate = parseDate(date)
  const parsedCompareDate = parseDate(compareDate)
  return isAfter(startOfDay(parsedDate), startOfDay(parsedCompareDate))
}

export const isDateBeforeOrEqualToDate = (date, compareDate) => {
  if (!date) return null
  const parsedDate = parseDate(date)
  const parsedCompareDate = parseDate(compareDate)
  return (
    isBefore(startOfDay(parsedDate), startOfDay(parsedCompareDate)) ||
    isEqual(startOfDay(parsedDate), startOfDay(parsedCompareDate))
  )
}

export const isDateAfterOrEqualToDate = (date, compareDate) => {
  if (!date) return null
  const parsedDate = parseDate(date)
  const parsedCompareDate = parseDate(compareDate)
  return (
    isAfter(startOfDay(parsedDate), startOfDay(parsedCompareDate)) ||
    isEqual(startOfDay(parsedDate), startOfDay(parsedCompareDate))
  )
}

export const formatDateDistance = (date, addSuffix = false) => {
  if (!date) return null
  const formattedDistance = formatDistanceToNowStrict(parseDate(date), {
    locale: getDateFnsLocal(),
    addSuffix,
  })
  return formattedDistance
}

export const formatDateDistanceFromToday = (
  date,
  addSuffix = false,
  useTodayLabel = null,
) => {
  if (!date) return null
  const parsedDate = parseDate(date)
  if (!parsedDate) return null
  const dateOnly = new Date(
    parsedDate.getFullYear(),
    parsedDate.getMonth(),
    parsedDate.getDate(),
  )
  if (useTodayLabel && isToday(dateOnly)) return useTodayLabel

  const today = new Date()
  const todayOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  )

  const formattedDistance = formatDistance(dateOnly, todayOnly, {
    locale: getDateFnsLocal(),
    addSuffix,
  })
  return formattedDistance
}

export const formatDayWeek = (date, isShort = false) => {
  if (!date) return null
  return format(
    parseDate(date),
    isShort ? IntlFormats.DAY_WEEK_SHORT : IntlFormats.DAY_WEEK_LONG,
    {
      locale: getDateFnsLocal(),
    },
  )
}

export const formatMonth = (date, isShort = false) => {
  if (!date) return null
  return format(
    parseDate(date),
    isShort ? IntlFormats.MONTH_SHORT : IntlFormats.MONTH_LONG,
    {
      locale: getDateFnsLocal(),
    },
  )
}

export const formatMonthYear = (date, isShort = false) => {
  if (!date) return null
  return format(
    parseDate(date),
    isShort
      ? `${IntlFormats.MONTH_SHORT} ${IntlFormats.YEAR_SHORT}`
      : `${IntlFormats.MONTH_LONG} ${IntlFormats.YEAR_LONG}`,
    {
      locale: getDateFnsLocal(),
    },
  )
}

export const weekStartsOn = () => {
  const currentLocale = getDateFnsLocal()
  return currentLocale?.options?.weekStartsOn ?? 0
}

export default formatDate
